import React, { useState, useEffect } from "react";
import {
  Box,
  Stack,
  Typography,
  TextField,
  Button,
  FormHelperText,
} from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DropdownOptions } from "../common/Dropdown/Dropdown";
import dayjs, { Dayjs } from "dayjs";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import "moment/locale/de";
import RadioChipWithLabel from "../common/RadioChipWithLabel/RadioChipWithLabel";
import ChipRadioGroup from "../common/RadioChip/RadioChip";
import LoadingButton from "@mui/lab/LoadingButton";
import AutoCompleteSelect from "../common/autocompleteSelect/AutoCompleteSelect";
import "./styles.scss";
import {
  createTransaction,
  updateTransaction,
  getAccountByClientId,
  getClients,
  voidTransaction,
} from "../../services/transactionService";
import {
  convertToLocal,
  convertToLocalYYYY_MM_DD,
} from "../../utils/dateFormat";
import { validateCreateTransactionBody } from "../../utils/validators";
import { setAccountDetails } from "../../redux/features/clientDetailsSlice";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../redux/store";
import {
  ACHTransaction,
  BaseTransaction,
  CheckTransaction,
  TransactionMethod,
  TransferTransaction,
  WireTransaction,
  UpdateTransaction
} from "../../types/transactionType";
import AddressComp, { Address } from "../addressComp/AddressComp";
import { PAYMENT_METHODS } from "../../variables/constants";
import {
  TransactionDataType,
  TransactionDetailsType,
} from "../../types/genericType";
import { generateClientName } from "../../utils/utils";
import AmountInputField from "../common/NumericField/AmountField";
import { formatDollar } from "../../utils/utils";
import { getLatestBook } from "../../services/booksService";

type NewTransactionProps = {
  setDrawerOpen: Function;
  clientOptions?: any[];
  setOnTxnSuccess: Function;
  setSnackbarMsg: Function;
  editFlow: boolean;
  selectedTransaction: TransactionDetailsType | null;
  handleDrawerClose: Function;
  getAllTransactions: Function;
  transactionResponse: TransactionDataType[];
};

const NewTransaction = (props: NewTransactionProps) => {
  const {
    setOnTxnSuccess,
    setSnackbarMsg,
    editFlow,
    selectedTransaction,
    handleDrawerClose,
    getAllTransactions,
    transactionResponse,
  } = props;

  const selectedMethod = () => {
    if (selectedTransaction?.checkNumber) {
      return "Check";
    } else if (selectedTransaction?.wireNumber) {
      return "Wire";
    } else if (selectedTransaction?.achDetails) {
      return "ACH";
    }
  };

  const [toClientOptions, setToClientOptions] = useState<any[]>();
  const [accountOptions, setAccountOptions] = useState<any[]>([]);
  const [toAccountOptions, setToAccountOptions] = useState<any[]>([]);
  const [fromClientId, setFromClientId] = useState<string | null>(
    selectedTransaction?.fromClientId ?? null
  );
  const [account, setAccount] = useState<number>(
    editFlow && selectedTransaction?.account ? selectedTransaction?.account : 0
  );
  const [accountBalance, setAccountBalance] = useState<number | null>();
  const [date, setDate] = useState<Dayjs | null>(
    selectedTransaction?.date && selectedTransaction?.date !== ""
      ? convertToLocal(dayjs(selectedTransaction?.date))
      : dayjs(new Date())
  );
  const [transactionType, setTransactionType] = useState<string>(
    selectedTransaction?.code ?? "Deposit"
  );
  const [method, setMethod] = useState<TransactionMethod>("Check");
  const [amount, setAmount] = useState<string>(
    editFlow && selectedTransaction?.amount
      ? selectedTransaction?.amount.toString()
      : ""
  );
  const [description, setDescription] = useState<string | null>(
    selectedTransaction?.description ?? null
  );
  const [bankTransactionType, setBankTransactionType] = useState<string | undefined>(
    selectedTransaction?.bankTransactionType
  );
  const [wireNumber, setWireNumber] = useState<string | null>(null);
  const [toClient, setToClient] = useState<string | null>(null);
  const [toAccount, setToAccount] = useState<number | null>(null);
  const [payee, setPayee] = useState<string | null>(
    selectedTransaction?.checkDetails?.payee ?? ""
  );
  const [checkMemo, setCheckMemo] = useState<string | null>(
    selectedTransaction?.checkDetails?.number ?? ""
  );
  const [achDetails, setAchDetails] = useState<string | null>(
    selectedTransaction?.achDetails ?? null
  );
  const [status, setStatus] = useState<string>(
    selectedTransaction?.status ?? "Outstanding"
  );
  const [isLoading, setIsLoading] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [addressObj, setAddressObj] = useState<Address>({
    address: selectedTransaction?.checkDetails?.address ?? null,
    apartment: selectedTransaction?.checkDetails?.apartment ?? null,
    city: selectedTransaction?.checkDetails?.city ?? null,
    state: selectedTransaction?.checkDetails?.state ?? null,
    zip: selectedTransaction?.checkDetails?.zip ?? null,
    country: selectedTransaction?.checkDetails?.country ?? null,
  });
  const [isFormValid, setIsFormValid] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [clientOptions, setClientOptions] = useState<DropdownOptions[]>([]);
  const [accountType, setAccountType] = useState<string>("");
  const [transactionDatePickerMinDate, setTransactionDatePickerMinDate] = useState<Dayjs>(dayjs(new Date().setDate(new Date().getDate() - 30)));
  const [transactionDatePickerMaxDate, setTransactionDatePickerMaxDate] = useState<Dayjs>(dayjs(new Date().setDate(new Date().getDate() + 30)));  

  const dispatch = useDispatch();

  const { accountDetails } = useSelector(
    (state: RootState) => state.clientDetails
  );

  const getClientOptions = async () => {
    getClients()
      ?.then((res: any) => {
        const clients: DropdownOptions[] = [];
        res.forEach((client: any) => {
          if (editFlow || client.isActive) {
            clients.push({
              value: client.id,
              label: generateClientName(client),
            });
          }
        });
        setClientOptions(clients);
        setToClientOptions(clients);
      })
      .catch((err) => {
        console.log(err);
      });
  };

  const setTransactionDatePickerMinAndMaxDates = async () => {
    getLatestBook()
      ?.then((res) => {
        const currentDate = dayjs();
        const lastMonthDate = currentDate.subtract(1, "month");
        const nextMonthDate = currentDate.add(1, "month");

        const latestMonthEndDate = dayjs(new Date(res.year, res.month, 1));
        
        setTransactionDatePickerMinDate(latestMonthEndDate.isBefore(lastMonthDate) ? latestMonthEndDate : lastMonthDate);
        setTransactionDatePickerMaxDate(nextMonthDate);
      })
      .catch((err) => {
        console.log(err);
      });
  };

  useEffect(() => {
    getClientOptions();
    setTransactionDatePickerMinAndMaxDates();
  }, []);

  const resetToAccountOptions = () => {
    const filteredAccounts = accountOptions.filter(
      (acc) => acc.value !== account
    );
    setToAccountOptions(filteredAccounts);
  };

  useEffect(() => {
    setIsFormValid(true);
    setError("");
  }, [fromClientId, toClient, account, toAccount, amount, payee, wireNumber]);

  useEffect(() => {
    if (fromClientId) {
      setAccountBalance(null);
      setAccountOptions([]);
      getAccountByClientId(fromClientId)
        ?.then((res: any) => {
          dispatch(setAccountDetails(res));
          const accounts: any[] = [];
          res?.forEach((acc: any) => {
            if (editFlow || acc.isActive) {
              accounts.push({
                value: acc.id,
                label: `${acc.name} - ${acc.accountNumber}`,
              });
            }
          });
          setAccountOptions(accounts);
          if (accounts.length <= 1) {
            const filteredClients = clientOptions.filter(
              (client) => client.value !== fromClientId
            );
            setToClientOptions(filteredClients);
          }
        })
        .catch((err: any) => {
          console.log(err);
        });
    }
  }, [fromClientId]);

  useEffect(() => {
    if (toClient) {
      setToAccountOptions([]);
      if (fromClientId === toClient && account) {
        resetToAccountOptions();
      } else {
        getAccountByClientId(toClient)
          ?.then((res: any) => {
            const accounts: any[] = [];
            res?.forEach((acc: any) => {
              if (editFlow || acc.isActive) {
                accounts.push({
                  value: acc.id,
                  label: `${acc.name} - ${acc.accountNumber}`,
                });
              }
            });
            setToAccountOptions(accounts);
          })
          .catch((err: any) => {
            console.log(err);
          });
      }
    }
  }, [toClient]);

  useEffect(() => {
    if (
      fromClientId &&
      accountOptions.length > 0 &&
      selectedTransaction?.fromAccount
    ) {
      setAccount(selectedTransaction?.fromAccount);
    }
  }, [fromClientId, accountOptions, selectedTransaction]);

  useEffect(() => {
    if (account) {
      resetToAccountOptions();

      const accountBal = accountDetails.filter((acc) => acc.id === account)?.[0]
        ?.balance;
      const accType = accountDetails.filter((acc) => acc.id === account)?.[0]
        ?.accountType;
      setAccountType(accType ?? "");
      setTransactionType(accType === "Loan" ? selectedTransaction?.code === "Loan Advance" ? "loan-advance" : "loan-payment" : selectedTransaction?.code || "Deposit");

      setAccountBalance(accountBal);
    }
  }, [account]);

  useEffect(() => {
    if (editFlow && selectedTransaction?.id) {
      setFromClientId(selectedTransaction?.fromClientId);
      setWireNumber(selectedTransaction?.wireNumber ?? null);
      setPayee(selectedTransaction?.checkDetails?.payee ?? null);
      setCheckMemo(selectedTransaction?.checkDetails?.memo ?? null);
      setAddressObj({
        address: selectedTransaction?.checkDetails?.address ?? null,
        apartment: selectedTransaction?.checkDetails?.apartment ?? null,
        city: selectedTransaction?.checkDetails?.city ?? null,
        state: selectedTransaction?.checkDetails?.state ?? null,
        zip: selectedTransaction?.checkDetails?.zip ?? null,
        country: selectedTransaction?.checkDetails?.country ?? null,
      });
      setMethod(selectedMethod() ?? "Check");
      setAchDetails(selectedTransaction?.achDetails ?? null);
      setDate(
        selectedTransaction?.date
          ? dayjs(selectedTransaction?.date)
          : dayjs(new Date())
      );

      if (selectedTransaction.bankTransactionType) {
        setBankTransactionType(selectedTransaction.bankTransactionType);
      }
    }
  }, [selectedTransaction]);

  const getWithdrawalBody = (
    method: TransactionMethod
  ): Partial<CheckTransaction | WireTransaction | ACHTransaction> => {
    switch (method) {
      case "Cash":
        return {};
      case "Check":
        return {
          payee: payee ?? "",
          ...addressObj,
          country: "USA",
          memo: checkMemo,
        };
      case "Wire":
        return { wireNumber: wireNumber ?? "" };
      case "ACH":
        return { achDetails };
      default:
        throw new Error("Invalid withdrawal method");
    }
  };

  const createTransactionBody = (
    type: string
  ):
    | BaseTransaction
    | TransferTransaction
    | CheckTransaction
    | ACHTransaction
    | WireTransaction
    | null => {
    switch (type) {
      case "Deposit":
        return {
          account: account,
          date: date && convertToLocalYYYY_MM_DD(date),
          transactionType: 1,
          amount: parseFloat(amount ? amount : "0"),
          description: description,
          bankTransactionType: bankTransactionType,
        } as BaseTransaction;
      case "loan-payment":
        return {
          account: account,
          date: date && convertToLocalYYYY_MM_DD(date),
          transactionType: 3,
          amount: parseFloat(amount ? amount : "0"),
          description: description,
          bankTransactionType: bankTransactionType,
        } as BaseTransaction;
      case "Withdrawal":
        const withdrawalBody = getWithdrawalBody(method);
        return {
          account: account,
          date: (date && convertToLocalYYYY_MM_DD(date)) ?? "",
          transactionType: 2,
          amount: parseFloat(amount),
          description: description,
          bankTransactionType: bankTransactionType,
          ...withdrawalBody,
        };
      case "loan-advance":
        return {
          account: account,
          date: (date && convertToLocalYYYY_MM_DD(date)) ?? "",
          transactionType: 4,
          amount: parseFloat(amount),
          description: description,
          bankTransactionType: bankTransactionType,
        } as BaseTransaction;
      case "Transfer":
        return {
          account: account,
          date: date && convertToLocalYYYY_MM_DD(date),
          transactionType: 7,
          amount: parseFloat(amount),
          description: description,
          bankTransactionType: bankTransactionType,
          toAccount: toAccount,
        } as TransferTransaction;
      default:
        return null;
    }
  };

  const updateTransactionBody = (
    bankTransactionType: string | null | undefined
  ):
    | UpdateTransaction
    | null => {
    return {
      bankTransactionType: bankTransactionType
    } as UpdateTransaction;
  };

  const handleGenerateSuccessMsg = () => {
    const clientName = clientOptions.filter(
      (client) => client.value === fromClientId
    )?.[0]?.label;
    const toClientName = clientOptions.filter(
      (client) => client.value === toClient
    )?.[0]?.label;
    switch (transactionType) {
      case "Deposit":
        return `$${amount} deposit to ${clientName} initiated `;
      case "loan-payment":
        return `$${amount} loan-payment to ${clientName} initiated `;
      case "Withdrawal":
        return `$${amount} withdrawal from ${clientName} initiated`;
      case "loan-advance":
        return `$${amount} loan-advance from ${clientName} initiated`;
      case "Transfer":
        return `$${amount} transfer from ${clientName} to ${toClientName} initiated`;
      default:
        return;
    }
  };

  const handleGenerateUpdateTransactionSuccessMsg = () => {
    return "Transaction updated successfully.";
  };

  const handleCreateTransaction = () => {
    setIsLoading(true);
    const newTransactionPayload = createTransactionBody(transactionType);
    const isTransactionValid = validateCreateTransactionBody(
      newTransactionPayload
    );
    setIsFormValid(isTransactionValid);
    if (isTransactionValid) {
      createTransaction(
        createTransactionBody(transactionType),
        transactionType.toLocaleLowerCase(),
        method.toLocaleLowerCase()
      )
        ?.then(() => {
          handleDrawerClose();
          setIsLoading(false);
          setOnTxnSuccess(true);
          getAllTransactions();
          setSnackbarMsg(handleGenerateSuccessMsg());
        })
        .catch((err: any) => {
          setIsLoading(false);
          setError(err?.response?.data?.title);
        });
    } else {
      setIsLoading(false);
    }
  };

  const handleUpdateTransaction = () => {
    setIsUpdating(true);
    if (selectedTransaction?.id) {
      updateTransaction(
        updateTransactionBody(bankTransactionType),
        selectedTransaction.id,
      )
        ?.then(() => {
          handleDrawerClose();
          setIsUpdating(false);
          setOnTxnSuccess(true);
          getAllTransactions();
          setSnackbarMsg(handleGenerateUpdateTransactionSuccessMsg());
        })
        .catch((err: any) => {
          setIsLoading(false);
          setError(err?.response?.data?.title);
        });
    }
  };

  const handleVoidTransaction = () => {
    setIsLoading(true);
    selectedTransaction &&
      voidTransaction(selectedTransaction?.id)
        ?.then(() => {
          handleDrawerClose();
          setIsLoading(false);
          setOnTxnSuccess(true);
          getAllTransactions();
          setSnackbarMsg("Transaction voided successfully");
        })
        .catch((err: any) => {
          setIsLoading(false);
          setError(err?.response?.data?.errors?.id?.[0]);
        });
  };

  const getClientNameFromTransactionRes = () => {
    const selectedInvestorId = selectedTransaction?.toAccount;
    const selectedTransactionRes = transactionResponse.find(
      (t) => t.id === selectedInvestorId
    );
    const investor = selectedTransactionRes?.investor;
    return generateClientName(investor);
  };

  const getAccountNameFromTransactionRes = () => {
    const selectedInvestorId = selectedTransaction?.toAccount;
    const selectedTransactionRes = transactionResponse.find(
      (t) => t.id === selectedInvestorId
    );
    return selectedTransactionRes?.name + " - " + selectedTransactionRes?.accountNumber;
  };

  return (
    <Box className="new-transaction">
      <Stack spacing={3}>
        <AutoCompleteSelect
          items={clientOptions}
          onSelectItem={setFromClientId}
          selected={fromClientId?.toString() ?? ""}
          label="Client"
          placeholder="Select an client"
          disabled={editFlow}
          required
        />
        {fromClientId && (
          <>
            <AutoCompleteSelect
              items={accountOptions}
              onSelectItem={setAccount}
              selected={account?.toString() ?? ""}
              placeholder="Select an account"
              label="Account"
              required
              disabled={editFlow}
            />
            {accountBalance !== null && accountBalance !== undefined && (
              <Box className="account-box">
                <Typography className="account-title">
                  Account Balance:
                </Typography>
                <Typography className="account-balance">{`${formatDollar(
                  accountBalance
                )}`}</Typography>
              </Box>
            )}
            <Box className="date-picker-container">
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="Transaction Date"
                  className="date-picker-container"
                  slotProps={{ textField: { size: "small" } }}
                  value={date}
                  disabled={editFlow}
                  onChange={(newValue) => setDate(newValue)}
                  minDate={transactionDatePickerMinDate}
                  maxDate={transactionDatePickerMaxDate}
                  sx={{
                    "& .Mui-selected": {
                      color: "#FFFFFF",
                      borderRadius: 2,
                      borderWidth: 1,
                      borderColor: "#e91e63",
                      border: "1px solid",
                      backgroundColor: "#f8bbd0",
                    },
                  }}
                />
              </LocalizationProvider>
            </Box>
            <RadioChipWithLabel
              label="Transaction Type"
              value={transactionType}
              setValue={setTransactionType}
              options={
                accountType !== "Loan"
                  ? [
                    { value: "Deposit", label: "Deposit" },
                    { value: "Withdrawal", label: "Withdrawal" },
                    { value: "Transfer", label: "Transfer" },
                  ]
                  : [
                    { value: "loan-payment", label: "Loan Payment" },
                    { value: "loan-advance", label: "Loan Advance" },
                  ]
              }
              disabled={editFlow}
            />
            {transactionType === "Withdrawal" && (
              <ChipRadioGroup
                label="Method"
                value={method}
                setValue={setMethod}
                options={Object.values(PAYMENT_METHODS)}
                disabled={editFlow}
              />
            )}
            {transactionType === "Withdrawal" &&
              method === "Wire" && (
              <TextField
                id="outlined-helperText"
                label="Wire Number"
                required
                size="small"
                value={wireNumber}
                onChange={(event) =>
                  setWireNumber(event.target.value as string)
                }
                className="textfield-width"
                disabled={editFlow}
              />
            )}
            {transactionType === "Withdrawal" &&
              method === "Check" && (
              <>
                <TextField
                  id="outlined-helperText"
                  label="Payee"
                  size="small"
                  value={payee}
                  onChange={(event) => setPayee(event.target.value as string)}
                  fullWidth
                  disabled={editFlow}
                  required
                />
                <AddressComp
                  address={addressObj}
                  setAddress={setAddressObj}
                  disabled={editFlow}
                />
                <TextField
                  id="outlined-helperText"
                  label="Check Memo"
                  size="small"
                  value={checkMemo}
                  onChange={(event) =>
                    setCheckMemo(event.target.value as string)
                  }
                  fullWidth
                  disabled={editFlow}
                />
              </>
            )}
            {transactionType === "Withdrawal" &&
              method === "ACH" && (
              <TextField
                id="outlined-helperText"
                label="ACH Details"
                size="small"
                value={achDetails}
                onChange={(event) =>
                  setAchDetails(event.target.value as string)
                }
                fullWidth
                required
                disabled={editFlow}
              />
            )}
            {!editFlow && transactionType === "Transfer" && (
              <>
                <AutoCompleteSelect
                  items={toClientOptions as DropdownOptions[]}
                  onSelectItem={setToClient}
                  selected={toClient?.toString() ?? ""}
                  label="Transfer To (Client)"
                  placeholder="Select a client"
                  disabled={editFlow}
                  required
                />
                <AutoCompleteSelect
                  selected={toAccount?.toString() ?? ""}
                  onSelectItem={setToAccount}
                  required
                  items={toAccountOptions}
                  label="Transfer To (Account)"
                  placeholder="Select an account"
                  disabled={editFlow}
                />
              </>
            )}
            {editFlow &&
              transactionType === "Transfer" &&
              selectedTransaction?.toAccount && (
              <>
                <TextField
                  id="outlined-helperText"
                  label="Transfer To (Client)"
                  value={getClientNameFromTransactionRes()}
                  size="small"
                  disabled={editFlow}
                  fullWidth
                />
                <TextField
                  id="outlined-helperText"
                  label="Transfer To (Account)"
                  value={getAccountNameFromTransactionRes()}
                  size="small"
                  disabled={editFlow}
                  fullWidth
                />
              </>
            )}
            <AmountInputField
              amount={amount}
              setAmount={setAmount}
              required  
              disabled={editFlow}
              error={
                !editFlow &&
                accountType === "Investment" &&
                ["Withdrawal", "Transfer"].includes(transactionType) &&
                (Number(Number(amount)?.toFixed(2)) >
                  Number(Number(accountBalance)?.toFixed(2)))
              }
            />
            {!editFlow &&
              accountType === "Investment" &&
              ["Withdrawal", "Transfer"].includes(transactionType) &&
              (Number(Number(amount)?.toFixed(2)) >
                Number(Number(accountBalance)?.toFixed(2))) && (
              <FormHelperText error>
                  Withdrawl Amount exceed your Account Balance this will take
                  your account to negtaive balance.
              </FormHelperText>
            )}
            <TextField
              id="outlined-helperText"
              label="Description"
              value={description}
              onChange={(event) => setDescription(event.target.value as string)}
              size="small"
              disabled={editFlow}
            />

            { transactionType !== "Transfer" && (
              <Box onDoubleClick={() => setBankTransactionType(undefined)}>
                <ChipRadioGroup
                  label="Bank Transaction"
                  value={bankTransactionType ?? ""}
                  setValue={setBankTransactionType}
                  options={["Credit", "Debit"]}
                />
              </Box>
            )}

            {editFlow && (
              <ChipRadioGroup
                label="Status"
                value={status}
                setValue={setStatus}
                options={["Outstanding", "Reconciled", "Voided"]}
                disabled={editFlow}
              />
            )}
            {!isFormValid && (
              <Typography variant="sFormHelper" className="error-text">
                {!isFormValid ? "Please fill all the required fields" : ""}
              </Typography>
            )}
          </>
        )}
        {error && (
          <Typography variant="sFormHelper" className="error-text">
            {error}
          </Typography>
        )}
        <Stack direction="row" spacing={2}>
          {!editFlow && fromClientId && (
            <LoadingButton
              variant="contained"
              data-testid="createTxnBtn"
              size="small"
              className="create-button"
              onClick={handleCreateTransaction}
              loading={isLoading}
            >
              Create Transaction
            </LoadingButton>
          )}
          {transactionType !== "Transfer" && editFlow && fromClientId && (
            <LoadingButton
              variant="contained"
              data-testid="updateTxnBtn"
              size="small"
              className="update-button"
              onClick={handleUpdateTransaction}
              loading={isUpdating}
            >
              Update Transaction
            </LoadingButton>
          )}
          {editFlow && (
            <LoadingButton
              variant="contained"
              data-testid="createTxnBtn"
              size="small"
              className="void-button"
              onClick={handleVoidTransaction}
              loading={isLoading}
            >
              VOID
            </LoadingButton>
          )}
          {(fromClientId || editFlow) && (
            <Button
              className="cancel-button"
              onClick={() => handleDrawerClose()}
            >
              CANCEL
            </Button>
          )}
        </Stack>
      </Stack>
    </Box>
  );
};

export default NewTransaction;
